import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { CATEGORIES, categoryLabel } from './categories';
import { useAuth } from './AuthContext';
import { formatAdded } from './format';
import './admin.css';

export default function AdminDashboard() {
  const [documents, setDocuments] = useState([]);
  const [status, setStatus] = useState('loading');
  const { checkSession } = useAuth();

  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl('/documents.php?all=1'), { credentials: 'include' })
      .then((res) => {
        if (res.status === 401) {
          // Session expired (or was never valid) while on this page —
          // re-sync auth state so AdminProtectedRoute redirects to login
          // instead of leaving a permanent "unable to load" message.
          checkSession();
          throw new Error('Session expired.');
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (!data || data.success !== true) throw new Error();
        setDocuments(Array.isArray(data.documents) ? data.documents : []);
        setStatus('success');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [checkSession]);

  const summary = useMemo(() => {
    const total = documents.length;
    const active = documents.filter((d) => d.is_active).length;
    const perCategory = CATEGORIES.map((c) => ({
      ...c,
      count: documents.filter((d) => d.category === c.value).length,
    }));
    return {
      total,
      active,
      inactive: total - active,
      inUse: perCategory.filter((c) => c.count > 0).length,
      perCategory,
      noDate: documents.filter((d) => !d.document_date).length,
      recent: [...documents]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 6),
    };
  }, [documents]);

  const stats = [
    { icon: 'bi-files', label: 'Total documents', value: summary.total, tone: 'gray' },
    { icon: 'bi-check-circle', label: 'Active', value: summary.active, tone: 'green' },
    { icon: 'bi-eye-slash', label: 'Inactive', value: summary.inactive, tone: 'amber' },
    { icon: 'bi-tags', label: 'Categories in use', value: summary.inUse, tone: 'blue' },
  ];

  return (
    <>
      <div className="adm-page-head">
        <div>
          <h1 className="adm-title">Dashboard</h1>
          <p className="adm-subtitle">An overview of the documents on your website.</p>
        </div>
        <Link className="adm-btn adm-btn-primary" to="/admin/documents">
          <i className="bi bi-cloud-arrow-up" />
          Upload document
        </Link>
      </div>

      {status === 'loading' && (
        <div className="adm-state">
          <span className="adm-spinner" /> Loading dashboard…
        </div>
      )}
      {status === 'error' && (
        <div className="adm-alert adm-alert-error" role="alert">
          <i className="bi bi-exclamation-circle" />
          <span>Unable to load dashboard data. Please try again.</span>
        </div>
      )}

      {status === 'success' && (
        <>
          <div className="adm-stats">
            {stats.map((s) => (
              <div className="adm-stat" key={s.label}>
                <span className={`adm-stat-icon tone-${s.tone}`}>
                  <i className={`bi ${s.icon}`} />
                </span>
                <div>
                  <div className="adm-stat-value">{s.value}</div>
                  <div className="adm-stat-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {summary.noDate > 0 && (
            <div className="adm-alert adm-alert-warn">
              <i className="bi bi-calendar-x" />
              <span>
                {summary.noDate} {summary.noDate === 1 ? 'document has' : 'documents have'} no date, so{' '}
                {summary.noDate === 1 ? 'it appears' : 'they appear'} last in {summary.noDate === 1 ? 'its' : 'their'}{' '}
                category.{' '}
                <Link className="adm-link" to="/admin/documents?filter=nodate">
                  Review
                </Link>
              </span>
            </div>
          )}

          <div className="adm-grid-2">
            <div className="adm-card">
              <div className="adm-card-head">
                <h2 className="adm-card-title">Recent uploads</h2>
                <Link className="adm-link" to="/admin/documents">
                  View all
                </Link>
              </div>
              {summary.recent.length === 0 ? (
                <div className="adm-empty">No documents yet.</div>
              ) : (
                <ul className="adm-list">
                  {summary.recent.map((d) => (
                    <li className="adm-list-item" key={d.id}>
                      <span className="adm-file-icon">
                        <i className="bi bi-file-earmark-pdf" />
                      </span>
                      <div className="adm-list-main">
                        <div className="adm-list-title">{d.title}</div>
                        <div className="adm-list-sub">
                          {categoryLabel(d.category)} · Added {formatAdded(d.created_at)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="adm-card">
              <div className="adm-card-head">
                <h2 className="adm-card-title">By category</h2>
              </div>
              <div className="adm-card-body adm-bars">
                {summary.perCategory.map((c) => (
                  <div className="adm-bar-row" key={c.value}>
                    <div className="adm-bar-top">
                      <span>{c.label}</span>
                      <span className="adm-bar-count">{c.count}</span>
                    </div>
                    <div className="adm-bar-track">
                      <div
                        className="adm-bar-fill"
                        style={{ width: `${summary.total ? Math.max((c.count / summary.total) * 100, c.count ? 2 : 0) : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
